// Gedeelde cache voor de planningsschermen (Rooster, Capaciteit, Nog in te plannen, Dagstart).
//
// Aanleiding (Joris, 25 sep): "als ik op Rooster klik en weer weg en weer terug moet die
// opnieuw laden". De vier tabs zijn aparte routes; bij wisselen verdwijnt de pagina met al
// haar state en begon alles opnieuw (personen, taken, agenda: 3–10 s). Deze module leeft op
// moduleniveau, dus zolang de app open is: terug naar een tab toont meteen de vorige stand
// en ververst stil op de achtergrond (stale-while-revalidate).
//
// Spelregels
// - Sleutel = gebruiker + endpoint + genormaliseerde parameters. De gebruiker zit erin zodat
//   een accountwissel op een gedeelde machine nooit andermans stand laat zien; wisselt de
//   gebruiker, dan gaat de hele cache leeg.
// - `maxLeeftijd`: ouder dan dit = niet meer tonen, gewoon opnieuw laden.
//   `verversNa`: ouder dan dit = wél tonen, maar op de achtergrond verversen.
// - Elke sleutel heeft een generatie. Ongeldig maken of geforceerd verversen verhoogt die;
//   een lading die vóór dat moment vertrok landt dan niet meer in de cache (anders zet een
//   trage oude lading de stand van vóór je wijziging of verversing terug).
// - Een lading kan zeggen dat haar resultaat onvolledig is (`magBewaren`): dan krijgt de
//   aanroeper het wel, maar de cache niet — een agenda waarvan mailboxen niet gelezen
//   konden worden mag niet als "iedereen vrij" twee minuten blijven hangen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMsalInstance } from './msalSingleton';

export const MINUUT = 60 * 1000;
export const STANDAARD_MAX_LEEFTIJD = 5 * MINUUT;
export const STANDAARD_VERVERS_NA = 30 * 1000;
const MAX_REGELS = 80;

const store = new Map(); // sleutel -> { waarde, at }
const lopend = new Map(); // sleutel -> { belofte, gen }
const generaties = new Map(); // sleutel -> getal
let laatsteGebruiker = null;

const genVan = (sleutel) => generaties.get(sleutel) || 0;
const verhoogGen = (sleutel) => generaties.set(sleutel, genVan(sleutel) + 1);
/** Lopende lading die nog geldig is (niet vervallen door ongeldig maken of forceren). */
const geldigLopend = (sleutel) => {
  const l = lopend.get(sleutel);
  return l && l.gen === genVan(sleutel) ? l : null;
};

/** E-mail van het actieve MSAL-account (klein), of '' buiten een ingelogde sessie. */
export function huidigeGebruiker() {
  try {
    const inst = getMsalInstance();
    const acc = inst?.getActiveAccount?.() || null;
    return String(acc?.username || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

const normaliseer = (waarde) => {
  if (Array.isArray(waarde)) {
    return waarde.map(normaliseer).map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).sort();
  }
  if (waarde && typeof waarde === 'object') {
    const uit = {};
    Object.keys(waarde).sort().forEach((k) => {
      if (waarde[k] !== undefined && waarde[k] !== null && waarde[k] !== '') uit[k] = normaliseer(waarde[k]);
    });
    return uit;
  }
  return typeof waarde === 'string' ? waarde.trim() : waarde;
};

/**
 * Cachesleutel: gebruiker | endpoint | parameters (sleutels gesorteerd, lijsten gesorteerd,
 * lege waarden weg). E-mailadressen maakt de aanroeper zelf klein. Zelfde verzoek vanaf twee schermen = zelfde sleutel.
 * Wisselt de gebruiker sinds de vorige sleutel, dan gaat de cache eerst leeg.
 */
export function planningSleutel(endpoint, params = {}) {
  const gebruiker = huidigeGebruiker();
  if (laatsteGebruiker !== null && laatsteGebruiker !== gebruiker) leegPlanningCache();
  laatsteGebruiker = gebruiker;
  return `${gebruiker}|${endpoint}|${JSON.stringify(normaliseer(params))}`;
}

/** Endpoint-deel van een sleutel (voor gericht ongeldig maken). */
const endpointVan = (sleutel) => String(sleutel).split('|')[1] || '';

/**
 * Wat er voor deze sleutel ligt, of null als er niets (bruikbaars) is.
 * @returns {{ waarde, leeftijd: number } | null}
 */
export function leesCache(sleutel, { maxLeeftijd = STANDAARD_MAX_LEEFTIJD } = {}) {
  const regel = store.get(sleutel);
  if (!regel) return null;
  const leeftijd = Date.now() - regel.at;
  if (leeftijd > maxLeeftijd) {
    store.delete(sleutel);
    return null;
  }
  return { waarde: regel.waarde, leeftijd };
}

function zet(sleutel, waarde) {
  store.delete(sleutel); // opnieuw achteraan: simpele LRU-achtige begrenzing
  store.set(sleutel, { waarde, at: Date.now() });
  while (store.size > MAX_REGELS) store.delete(store.keys().next().value);
}

/**
 * Optimistisch bijwerken: vervang de waarde van een bestaande regel en houd de leeftijd
 * vast (de server heeft het nog niet bevestigd; bij terugkeren wordt er dus nog steeds
 * geverifieerd). Bestaat de regel niet (meer), dan gebeurt er niets.
 */
export function werkCacheBij(sleutel, waardeOfFunctie) {
  const regel = store.get(sleutel);
  if (!regel) return;
  const nieuw = typeof waardeOfFunctie === 'function' ? waardeOfFunctie(regel.waarde) : waardeOfFunctie;
  store.set(sleutel, { waarde: nieuw, at: regel.at });
}

/** Lopende lading voor deze sleutel (voor een "ververst…"-indicatie), of null. */
export function lopendeLading(sleutel) {
  return geldigLopend(sleutel)?.belofte || null;
}

function start(sleutel, fetcher, magBewaren) {
  const gen = genVan(sleutel);
  const belofte = (async () => {
    try {
      const waarde = await fetcher();
      if (genVan(sleutel) !== gen) {
        // Ondertussen ongeldig gemaakt of geforceerd ververst: deze uitkomst is ouder dan
        // wat de gebruiker inmiddels wil zien. Niet bewaren, en de aanroeper krijgt de
        // nieuwere stand (lopend of bewaard) in plaats van deze.
        const nieuwer = geldigLopend(sleutel);
        if (nieuwer) return nieuwer.belofte;
        const bewaard = store.get(sleutel);
        return bewaard ? bewaard.waarde : waarde;
      }
      if (!magBewaren || magBewaren(waarde)) zet(sleutel, waarde);
      return waarde;
    } finally {
      if (lopend.get(sleutel)?.gen === gen) lopend.delete(sleutel);
    }
  })();
  lopend.set(sleutel, { belofte, gen });
  return belofte;
}

/**
 * Stale-while-revalidate ophalen.
 *
 * - Ligt er iets jonger dan `verversNa`: dat, zonder netwerk.
 * - Ligt er iets tussen `verversNa` en `maxLeeftijd`: dat, direct; op de achtergrond wordt
 *   ververst en `bijVerversing(nieuw)` aangeroepen als die lading slaagt en nog geldig is.
 * - Anders (of met `forceer`): één (gedeelde) lading, en daarop wachten.
 * - `verversFetcher` (optioneel): gebruikt voor die achtergrondverversing i.p.v. `fetcher`,
 *   bv. om de server óók zijn eigen cache te laten overslaan (agenda: vers=1).
 *
 * @returns {Promise<any>}
 */
export function haal(sleutel, fetcher, {
  maxLeeftijd = STANDAARD_MAX_LEEFTIJD,
  verversNa = STANDAARD_VERVERS_NA,
  forceer = false,
  magBewaren = null,
  bijVerversing = null,
  verversFetcher = null,
} = {}) {
  if (forceer) {
    verhoogGen(sleutel);
    return start(sleutel, fetcher, magBewaren);
  }
  const bewaard = leesCache(sleutel, { maxLeeftijd });
  if (bewaard) {
    if (bewaard.leeftijd > verversNa) {
      const al = geldigLopend(sleutel);
      const achtergrond = al ? al.belofte : start(sleutel, verversFetcher || fetcher, magBewaren);
      const gen = al ? al.gen : genVan(sleutel);
      achtergrond
        .then((nieuw) => { if (bijVerversing && genVan(sleutel) === gen) bijVerversing(nieuw); })
        .catch(() => { /* stil: de getoonde stand blijft staan, de volgende keer opnieuw */ });
    }
    return Promise.resolve(bewaard.waarde);
  }
  const al = geldigLopend(sleutel);
  if (al) return al.belofte;
  return start(sleutel, fetcher, magBewaren);
}

/**
 * Geforceerd vers ophalen voor een eigen stille ronde, mét uitslag of die nog geldig is.
 *
 * Anders dan `haal({ forceer })` krijgt de aanroeper altijd zijn eigen antwoord terug plus
 * `geldig`: false betekent dat de sleutel sinds het vertrek ongeldig is gemaakt of opnieuw
 * geforceerd (een wijziging, een verversing). Zo'n antwoord landt niet in de cache en hoort
 * de aanroeper ook niet te tonen — anders zet een ronde die vóór je wijziging vertrok de
 * oude stand terug. De lading staat zolang als lopend geregistreerd, zodat `maakOngeldig`
 * haar ook ziet als de regel zelf al uit de cache was.
 *
 * @returns {Promise<{ waarde: any, geldig: boolean }>}
 */
export function haalVers(sleutel, fetcher, { magBewaren = null } = {}) {
  verhoogGen(sleutel);
  const gen = genVan(sleutel);
  const uitslag = (async () => {
    try {
      const waarde = await fetcher();
      const geldig = genVan(sleutel) === gen;
      if (geldig && (!magBewaren || magBewaren(waarde))) zet(sleutel, waarde);
      return { waarde, geldig };
    } finally {
      if (lopend.get(sleutel)?.gen === gen) lopend.delete(sleutel);
    }
  })();
  // Wie via `haal` aanhaakt krijgt dezelfde regels als bij `start`: een vervallen uitkomst
  // wordt vervangen door de nieuwere stand (lopend of bewaard).
  const voorAnderen = uitslag.then(({ waarde, geldig }) => {
    if (geldig) return waarde;
    const nieuwer = geldigLopend(sleutel);
    if (nieuwer) return nieuwer.belofte;
    const bewaard = store.get(sleutel);
    return bewaard ? bewaard.waarde : waarde;
  });
  voorAnderen.catch(() => {}); // niemand aangehaakt: geen unhandled rejection
  lopend.set(sleutel, { belofte: voorAnderen, gen });
  return uitslag;
}

/**
 * Ongeldig maken na een wijziging. `endpoints`: één endpoint of een lijst (vergeleken met
 * het endpoint-deel van de sleutel), of een functie (sleutel) => boolean.
 * `behalve`: sleutels die blijven staan (het scherm dat zelf net optimistisch bijwerkte),
 * maar waarvan een lopende lading wél vervalt — die vertrok vóór de wijziging.
 */
export function maakOngeldig(endpoints, { behalve = [] } = {}) {
  const lijst = Array.isArray(endpoints) ? endpoints : [endpoints];
  const past = typeof endpoints === 'function'
    ? endpoints
    : (sleutel) => lijst.includes(endpointVan(sleutel));
  const blijft = new Set(behalve.filter(Boolean));
  const alle = new Set([...store.keys(), ...lopend.keys()]);
  alle.forEach((sleutel) => {
    if (!past(sleutel)) return;
    verhoogGen(sleutel);
    if (!blijft.has(sleutel)) store.delete(sleutel);
  });
}

/** Verse waarde direct bewaren (bv. een eigen stille ronde die al binnen is). */
export function zetCache(sleutel, waarde) {
  zet(sleutel, waarde);
}

/**
 * Buffer met de vorm van createWeekCache (get/set/load/prefetch/delete/clear), maar op de
 * gedeelde cache: overleeft het wisselen van tab. `lokaal` is de eigen sleutel van het
 * scherm (bv. "2026-09-28|2026-10-02"); die wordt binnen `endpoint` + gebruiker gezet.
 * Zelfde generatieregels: wat vóór een delete/clear vertrok, landt niet meer.
 */
export function gedeeldeBuffer(endpoint, { maxLeeftijd = STANDAARD_MAX_LEEFTIJD } = {}) {
  const k = (lokaal) => planningSleutel(endpoint, { venster: lokaal });
  return {
    get: (lokaal) => leesCache(k(lokaal), { maxLeeftijd })?.waarde,
    leeftijd: (lokaal) => leesCache(k(lokaal), { maxLeeftijd })?.leeftijd ?? null,
    set: (lokaal, waarde) => zet(k(lokaal), waarde),
    load: (lokaal, loader, { magBewaren = null } = {}) => haal(k(lokaal), () => loader(lokaal), { maxLeeftijd, verversNa: Infinity, magBewaren }),
    /** Eigen stille ronde (zie haalVers): { waarde, geldig }. */
    ververs: (lokaal, loader, { magBewaren = null } = {}) => haalVers(k(lokaal), () => loader(lokaal), { magBewaren }),
    prefetch(lokaal, loader, opties) {
      if (leesCache(k(lokaal), { maxLeeftijd }) || geldigLopend(k(lokaal))) return;
      this.load(lokaal, loader, opties).catch(() => {});
    },
    delete: (lokaal) => { const sl = k(lokaal); maakOngeldig((x) => x === sl); },
    clear: () => maakOngeldig(endpoint),
  };
}

/** Alles weg (uitloggen, accountwissel, tests). */
export function leegPlanningCache() {
  new Set([...store.keys(), ...lopend.keys()]).forEach(verhoogGen);
  store.clear();
  lopend.clear();
  laatsteGebruiker = null;
}

/**
 * Stille ververs-indicatie: `bezig` wordt pas true als een lading langer dan `drempelMs`
 * duurt. Snel verversen blijft onzichtbaar; alleen een trage ronde krijgt een subtiel
 * teken. `volg(belofte)` mag vaker aangeroepen worden; bezig tot alle gevolgde klaar zijn.
 */
export function useTraagVerversen(drempelMs = 1000) {
  const [bezig, setBezig] = useState(false);
  const lopendRef = useRef(new Set());
  const timer = useRef(null);
  const levend = useRef(true);
  useEffect(() => {
    levend.current = true;
    return () => { levend.current = false; clearTimeout(timer.current); };
  }, []);
  const volg = useCallback((belofte) => {
    if (!belofte) return;
    const set = lopendRef.current;
    set.add(belofte);
    if (!timer.current) {
      timer.current = setTimeout(() => {
        timer.current = null;
        if (levend.current && set.size) setBezig(true);
      }, drempelMs);
    }
    const klaar = () => {
      set.delete(belofte);
      if (set.size) return;
      clearTimeout(timer.current);
      timer.current = null;
      if (levend.current) setBezig(false);
    };
    Promise.resolve(belofte).then(klaar, klaar);
  }, [drempelMs]);
  return { bezig, volg };
}
